// src/components/3DModelling/ViewModel.js
import React, { useRef, useState } from 'react';
import { downloadJSON, log, warn, error } from './../../utils/loggingUtils';
import { availableMaterials, applyMaterialToMesh, MeshGroups } from './../../utils/materialUtils';
import { useBabylonScene } from '../../hooks/useBabylonScene';
import MaterialSelector from './MaterialSelector';

const ViewModel = () => {
  // References to canvas and Babylon.js engine/scene
  const canvasRef = useRef(null);
  const floorMeshRef = useRef(null);
  const engineRef = useRef(null);
  const sceneRef = useRef(null);

  // State variables
  const [floorType, setFloorType] = useState('Carpet');
  const [walls, setWalls] = useState([]);
  const [objects, setObjects] = useState([]);
  const [doors, setDoors] = useState([]);      
  const [windows, setWindows] = useState([]);  
  const [isFloorsOpen, setIsFloorsOpen] = useState(false);
  const [isWallsOpen, setIsWallsOpen] = useState(false);
  const [isObjectsOpen, setIsObjectsOpen] = useState(false);
  const [isDoorsOpen, setIsDoorsOpen] = useState(false);
  const [isWindowsOpen, setIsWindowsOpen] = useState(false);
  const [hoveredMeshInfo, setHoveredMeshInfo] = useState(null);
  const [selectedMeshInfo, setSelectedMeshInfo] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [substitutionPanel, setSubstitutionPanel] = useState({ isOpen: false, mesh: null });

  // List of preloaded objects that can be added to the scene
  const availableComponents = ['sideboard_8.glb', 'sink.glb', 'bathtub.glb', 'toilet.glb'];

  // Initialize and manage the Babylon.js scene
  const { loadAdditionalObject } = useBabylonScene({
    canvasRef,
    floorType,
    floorMeshRef,
    setWalls,
    setObjects,
    setDoors,
    setWindows,
    setHoveredMeshInfo,
    setSelectedMeshInfo,
    setTooltipPos,
    sceneRef,
    engineRef
  });

  // Handler to open the substitution panel
  const handleSubstitute = (mesh) => {
    setSubstitutionPanel({ isOpen: true, mesh });
  };

  // Handler to change material for a mesh or hide it
const handleMaterialChange = (mesh, materialName) => {
	if (!sceneRef.current) {
	  warn('Scene not available.');
	  return;
	}
  
	// 1) Apply or hide the material at the Babylon level
	if (materialName === 'Hide') {
	  mesh.isVisible = false;
	  mesh.currentMaterial = 'Hidden';
	  log(`Mesh '${mesh.name}' has been hidden.`);
	} else {
	  const selectedMaterial = availableMaterials.find(mat => mat.name === materialName);
	  if (selectedMaterial) {
		mesh.isVisible = true;
		applyMaterialToMesh(mesh, sceneRef.current, selectedMaterial, log, warn);
		mesh.currentMaterial = materialName;  // So Babylon knows which material is applied
	  }
	}
  
	// 2) If it's a wall, update the React `walls` state with the new currentMaterial
	if (mesh.group === MeshGroups.WALL) {
	  setWalls(prevWalls =>
		prevWalls.map(wall => {
		  if (wall === mesh) {
			// Return a new object with updated currentMaterial
			return {
			  ...wall,
			  currentMaterial: mesh.currentMaterial
			};
		  }
		  return wall;
		})
	  );
	}
  
	// If you want to do the same for DOOR / WINDOW / etc., 
	// you can replicate this pattern for setDoors/setWindows.
  };
  
  const getAllowedMaterials = (mesh) => {
    if (!mesh.group) return availableMaterials; 
    let materials = availableMaterials.filter(mat => {
      const groupAllowed = mat.allowedGroups.includes(mesh.group);
      if (mat.allowedMeshes && mat.allowedMeshes.length > 0) {
        return groupAllowed && mat.allowedMeshes.includes(mesh.name);
      }
      return groupAllowed;
    });

    if (mesh.group === MeshGroups.DOOR || mesh.group === MeshGroups.WINDOW) {
      materials = ['Hide', ...materials.map(mat => mat.name)];
    } else {
      materials = materials.map(mat => mat.name);
    }

    return materials;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', width: '100vw', height: '100vh', margin: 0, padding: 0, position: 'relative' }}>
      {/* Settings Panel */}
      <div style={{
        width: '300px',
        backgroundColor: '#f4f4f4',
        padding: '20px',
        boxSizing: 'border-box',
        borderRight: '1px solid #ccc',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <div>
          <h2 style={{ marginTop: 0 }}>Settings</h2>
          
          {/* Floor Settings */}
          <div style={{ marginBottom: '20px' }}>
            <div className="collapsible-header" onClick={() => setIsFloorsOpen(!isFloorsOpen)} aria-expanded={isFloorsOpen}>
              <span className={`arrow ${isFloorsOpen ? 'open' : 'closed'}`}></span>
            </div>
              <div className="collapsible-content">
                <label htmlFor="floorSelect" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Floor Type:</label>
                <select
                  id="floorSelect"
                  value={floorType}
                  onChange={(e) => setFloorType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ccc'
                  }}
                >
                  <option value="Granite tiles">Granite tiles</option>
                  <option value="Dark grey carpet">Dark grey carpet</option>
                  <option value="Hardwood">Hardwood</option>
                  <option value="Slate">Slate</option>
                  <option value="Stone mosaic">Stone mosaic</option>
                  <option value="Terracotta tiles">Terracotta tiles</option>
                  <option value="Ceramics long white">Ceramics long white</option>
                </select>
              </div>
          </div>

          {/* Walls Settings */}
            <div style={{ marginBottom: '20px' }}>
              <div className="collapsible-header" onClick={() => setIsWallsOpen(!isWallsOpen)} aria-expanded={isWallsOpen}>
			  <label htmlFor="floorSelect" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Walls:</label>
              </div>
                <div className="collapsible-content">
                  {walls.map((wall, index) => (
                    <MaterialSelector
                      key={index}
                      label={wall.name}
                      mesh={wall}
                      allowedMaterials={getAllowedMaterials(wall)}
                      currentMaterial={wall.currentMaterial}
                      onChange={handleMaterialChange}
                    />
                  ))}
                </div>
            </div>
        </div>

        {/* Download Logs Button */}
        <button
          type="button"
          onClick={downloadJSON}
          style={{
            padding: '10px',
            fontSize: '14px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            backgroundColor: '#ddd',
            cursor: 'pointer'
          }}
        >
          Download GLTF json
        </button>
      </div>

      {/* 3D Canvas Area */}
      <div style={{ flexGrow: 1, position: 'relative' }}>
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />

        
      </div>
    </div>
  );
};

export default ViewModel;
